import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, TextField, Typography, Tooltip } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import sh from "bundles/common/utils/sh";
import CardDropIndicator from "./CardDropIndicator";
import { useTheme } from "@mui/styles";

import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { attachClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

const KanbanColumn = ({
  column,
  dragDisabled,
  columnDragDisabled = false,
  isDragging = false,
  cards,
  columnStats = {},
  columnCount,
  cardDropTarget = null,
  renderCard,
  setColumns,
  editingColumnId,
  setEditingColumnId,
  kanbanColumnsLength,
}) => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const ref = useRef(null);
  const cardDropRef = useRef(null);
  const dragHandleRef = useRef(null);
  const intl = useIntl();
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const theme = useTheme();

  const KANBAN_CARD_HEIGHT = 200;
  const KANBAN_CARD_GAP = 16;
  const KANBAN_VISIBLE_CARDS = 5;
  const cardGapPx = KANBAN_CARD_GAP;
  const cardsMaxHeightPx = KANBAN_VISIBLE_CARDS * KANBAN_CARD_HEIGHT + (KANBAN_VISIBLE_CARDS - 1) * KANBAN_CARD_GAP;

  const [editingName, setEditingName] = useState("");
  const cancelNextBlurRef = useRef(false);
  const renameInFlightRef = useRef(false);

  const isEditing = editingColumnId === column.id;

  const count = typeof columnCount === "number" ? columnCount : (Array.isArray(cards) ? cards.length : 0);

  const isCardDropTargetForThisColumn =
    cardDropTarget && String(cardDropTarget.columnId) === String(column.id);
  const showEndCardIndicator =
    Boolean(isCardDropTargetForThisColumn && cardDropTarget?.type === "column");

  const durationDays = useMemo(() => {
    const median = columnStats?.median_days;
    if (typeof median === "number") return median;
    return columnStats?.avg_days;
  }, [columnStats]);

  const passRate = columnStats?.pass_to_next_rate;

  const formatDaysShort = useCallback(
    (value) => {
      if (typeof value !== "number" || Number.isNaN(value)) return "—";
      return `${intl.formatNumber(value, { maximumFractionDigits: 2 })} ${intl.formatMessage({ id: "recruiter.kanban.days_short" })}`;
    },
    [intl]
  );

  const formatPercent0 = useCallback(
    (value) => {
      if (typeof value !== "number" || Number.isNaN(value)) return "—";
      return intl.formatNumber(value, { style: "percent", maximumFractionDigits: 0 });
    },
    [intl]
  );

  const startEditingColumnName = useCallback(() => {
    if (!setEditingColumnId) return;
    setEditingColumnId(column.id);
    setEditingName(column?.name || "");
  }, [column.id, column?.name, setEditingColumnId]);

  const cancelEditingColumnName = useCallback(() => {
    cancelNextBlurRef.current = true;
    if (setEditingColumnId) setEditingColumnId(null);
    setEditingName("");
  }, [setEditingColumnId]);

  const submitColumnName = useCallback(async () => {
    const nextName = (editingName || "").trim();
    if (!nextName) {
      cancelEditingColumnName();
      return;
    }

    const prevName = column?.name;
    if (setEditingColumnId) setEditingColumnId(null);

    if (!setColumns || typeof setColumns !== "function") return;
    if (nextName === prevName) return;
    if (!authenticityToken) return;
    if (renameInFlightRef.current) return;
    renameInFlightRef.current = true;

    setColumns((prev) => (prev || []).map((c) => (c.id === column.id ? { ...c, name: nextName } : c)));

    try {
      const res = await sh.put(`/kanban_column/${column.id}`,
        {
          name: nextName,
          authenticity_token: authenticityToken,
        });

      const serverName = res?.data?.name;
      if (serverName && serverName !== nextName) {
        setColumns((prev) => (prev || []).map((c) => (c.id === column.id ? { ...c, name: serverName } : c)));
      }
    } catch (e) {
      console.error(e);
      setColumns((prev) => (prev || []).map((c) => (c.id === column.id ? { ...c, name: prevName } : c)));
    } finally {
      renameInFlightRef.current = false;
    }
  }, [authenticityToken, cancelEditingColumnName, column.id, column?.name, editingName, setColumns, setEditingColumnId]);

  useEffect(() => {
    if (isEditing) setEditingName(column?.name || "");
  }, [isEditing, column?.name]);

  useEffect(() => {
    if (dragDisabled) return undefined;
    const el = cardDropRef.current;
    if (!el) return undefined;

    return dropTargetForElements({
      element: el,
      canDrop: ({ source }) => source?.data?.type === "kanban-card",
      getData: () => ({ type: "kanban-column-target", columnId: Number(column.id) }),
    });
  }, [column.id, dragDisabled]);

  useEffect(() => {
    if (dragDisabled) return undefined;
    const el = ref.current;
    const handleEl = dragHandleRef.current;
    if (!el) return undefined;

    const cleanups = [];

    cleanups.push(
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) => source?.data?.type === "kanban-column",
        getData: ({ input, element }) =>
          attachClosestEdge(
            { type: "kanban-column-drop-target", columnId: Number(column.id) },
            { input, element, allowedEdges: ["left", "right"] }
          ),
      })
    );

    if (!columnDragDisabled && handleEl) {
      cleanups.push(
        draggable({
          element: el,
          dragHandle: handleEl,
          getInitialData: () => ({ type: "kanban-column", columnId: Number(column.id) }),
        })
      );
    }

    return combine(...cleanups);
  }, [column.id, columnDragDisabled, dragDisabled]);

  return (
    <Box
      ref={ref}
      sx={{
        width: isDesktop ? "374px" : "280px",
        minWidth: isDesktop ? "374px" : "280px",
        borderRadius: "16px",
        border: `1px solid ${theme.palette.background.neutral}`,
        overflow: "hidden",
        backgroundColor: "#EEF1F4,0.12",
        opacity: isDragging ? 0.5 : 1,
        transition: "opacity 0.2s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 12px 0px",
          gap: "8px",
        }}
      >
        {/* Drag handle for non-protected columns */}
        {!columnDragDisabled && !dragDisabled && kanbanColumnsLength > 3 ? (
          <Box
            ref={dragHandleRef}
            sx={{
              cursor: "grab",
              display: "flex",
              alignItems: "center",
              color: theme.palette.secondary.black,
              opacity: 0.5,
              "&:hover": { opacity: 0.8 },
              "&:active": { cursor: "grabbing" },
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: "20px" }} />
          </Box>
        ) : (
          <Box sx={{ width: "20px" }} /> 
        )}
        {isEditing ? (
          <TextField
            value={editingName}
            size="small"
            autoFocus
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={() => {
              if (cancelNextBlurRef?.current) {
                cancelNextBlurRef.current = false;
                return;
              }
              submitColumnName();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitColumnName();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                cancelEditingColumnName();
              }
            }}
            inputProps={{ "aria-label": "column-name" }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                height: "32px",
                backgroundColor: "#FFF",
                borderRadius: "8px",
              },
            }}
          />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
            <Typography
              sx={{ fontWeight: 800, fontFamily: "Segoe UI Bold, sans-serif", fontSize: "16px", color: theme.palette.secondary.black, cursor: "text" }}
              title={column.name}
              onClick={startEditingColumnName}
            >
              {column.name}
            </Typography>
            <Tooltip title={intl.formatMessage({ id: "recruiter.kanban.edit_column_name" })} placement="top" arrow>
              <IconButton
                onClick={startEditingColumnName}
                sx={{ fontSize: "12px", color: theme.palette.secondary.black, opacity: 0.75, width: "20px", height: "20px" }}
              >
                <EditIcon style={{ width: "16px", height: "16px" }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Box sx={{ minWidth: "24px", minHeight: "24px", backgroundColor: theme.palette.secondary.box, borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif", fontSize: "14px", color: theme.palette.secondary.black, textAlign: "center" }}>
            {count}
          </Typography>
        </Box>
      </Box>

      <Box ref={cardDropRef} sx={{ padding: "12px 10px 14px", display: "flex", flexDirection: "column", minHeight: "340px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            backgroundColor: "#FFF",
            borderRadius: "10px",
            border: `1px solid ${theme.palette.background.neutral}`,
            padding: "8px 10px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif", color: theme.palette.secondary.black, opacity: 0.8 }}>
              <FormattedMessage id="recruiter.kanban.average_duration" />
            </Typography>
            <Typography sx={{ fontSize: "12px", color: theme.palette.secondary.black, fontWeight: 800, fontFamily: "Segoe UI Semibold, sans-serif" }}>
              {formatDaysShort(durationDays)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif", color: theme.palette.secondary.black, opacity: 0.8 }}>
              <FormattedMessage id="recruiter.kanban.pass_rate" />
            </Typography>
            <Typography sx={{ fontSize: "12px", color: theme.palette.secondary.black, fontWeight: 800, fontFamily: "Segoe UI Semibold, sans-serif" }}>
              {formatPercent0(passRate)}
            </Typography>
          </Box>
        </Box>

        {cards.length === 0 ? (
          <Box
            sx={{
              border: `1px dashed ${theme.palette.background.neutral}`,
              borderRadius: "12px",
              padding: "12px",
              color: theme.palette.secondary.black,
              opacity: 0.55,
              fontSize: "12px",
              textAlign: "center",
              position: "relative",
              marginTop: "16px",
            }}
          >
            <CardDropIndicator visible={showEndCardIndicator} position="top" />
            <FormattedMessage id="recruiter.kanban.drop_candidates_here" />
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: `${cardsMaxHeightPx}px`,
              overflowY: "auto",
              paddingRight: "4px",
              scrollbarWidth: "none",
              msOverflowStyle: "none", 
              "&::-webkit-scrollbar": { width: 0, height: 0 }, 
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: `${cardGapPx}px`, paddingTop: "16px" }}>
              {cards.map((card, idx) => {
                const showTopIndicator =
                  Boolean(
                    isCardDropTargetForThisColumn &&
                      cardDropTarget?.type === "card" &&
                      cardDropTarget?.index === idx &&
                      cardDropTarget?.edge === "top"
                  );
                const showBottomIndicator =
                  Boolean(
                    isCardDropTargetForThisColumn &&
                      cardDropTarget?.type === "card" &&
                      cardDropTarget?.index === idx &&
                      cardDropTarget?.edge === "bottom"
                  );

                return (
                  <Box key={card.id} sx={{ position: "relative" }}>
                    <CardDropIndicator visible={showTopIndicator} position="top" />
                    {renderCard(card, idx)}
                    <CardDropIndicator visible={showBottomIndicator} position="bottom" />
                  </Box>
                );
              })}

              {showEndCardIndicator ? (
                <Box sx={{ position: "relative", height: "12px" }}>
                  <CardDropIndicator visible={true} position="top" />
                </Box>
              ) : null}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default KanbanColumn;


